import {Icon} from '@wandb/weave/components/Icon';
import classNames from 'classnames';
import React from 'react';

import {keyFromString} from '../../../util/runs';
import {getIconNameForKey} from '../../../util/uihelpers';
import {useDragOver} from './useDragOver';

type TableEditorFixedColumnFieldProps = {
  children: React.ReactNode;
  column: string;
  fixed: true;
  pinned: true;
};

type TableEditorColumnFieldUnfixedProps = {
  children: React.ReactNode;
  column: string;
  draggingColumnName: string | null;
  fixed?: boolean;
  onDragStart: (column: string) => void;
  onDrop: (column: string) => void;
  onMoveClick: (column: string) => void;
  onPinClick: (column: string) => void;
  pinned?: boolean;
  hidden?: boolean;
};

type TableEditorColumnFieldProps =
  | TableEditorColumnFieldUnfixedProps
  | TableEditorFixedColumnFieldProps;

export function TableEditorColumnField({
  children,
  column,
  fixed,
  pinned,
  ...props
}: TableEditorColumnFieldProps) {
  const key = keyFromString(column)!;
  const {getDraggingProps, isDraggingOver} = useDragOver({
    onDragStart() {
      if ('onDragStart' in props) {
        props.onDragStart(column);
      }
    },
    onDrop() {
      if ('onDrop' in props) {
        props.onDrop(column);
      }
    },
  });

  const baseClasses = 'flex h-36 items-center gap-10 rounded-sm px-10 py-6';

  // Column is fixed and pinned—probably the "Disply Name" column. It cannot
  // be dragged or dropped, and it is always visible:
  if (fixed && pinned) {
    return (
      <div className={baseClasses}>
        <Icon name={getIconNameForKey(key)} />
        <div className="grow">{children}</div>
        <div>🔒</div>
        <div>📌</div>
      </div>
    );
  }

  const {draggingColumnName, hidden} =
    props as TableEditorColumnFieldUnfixedProps;

  const cls = classNames(baseClasses, {
    'cursor-grab': !fixed,
    'cursor-grabbing': draggingColumnName === column,
    'bg-moon-150': draggingColumnName === column,
  });

  return (
    <div style={{display: 'contents'}} {...getDraggingProps()}>
      {hidden ? null : (
        <div
          className={classNames('transition-all', {
            'h-36': isDraggingOver,
            'h-0': !isDraggingOver,
          })}
          aria-hidden
        />
      )}
      <div draggable="true" className={cls}>
        <Icon name={getIconNameForKey(key)} />
        <div className="grow">{children}</div>
        {fixed ? <div>🔒</div> : null}
        {pinned ? <div>📌</div> : null}
      </div>
    </div>
  );
}
